import { KeyboardEvent } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { Modal } from '../../../Modal';
import { Button, Input, TextButton } from '../../../symphony/ui';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';
import { useChangePasswordFlow } from './use-change-password';
import styles from './change-password.module.css';

// Figma "Symphony" → Change password (10666:94029). Three secure fields — current (+ a "Forgot
// password?" link), new (+ the strength card once typing starts) and confirm — over the shared modal
// shell, with Close / Save. The rules, the copy and the endpoint are the native apps' change-password
// screen; only the chrome differs (a modal here, a pushed screen there).
//
// Each field is the DS `Input` and NOTHING around it: `kind="secure"` carries the masking and the eye,
// `label` carries the `.Form Label`, `statusMessage` carries the `.Bottom Message`. The screen used to
// wrap the DS Input in its own label element, its own eye toggle and its own error row — which is how
// its error line came to draw TWO alert glyphs (the DS's plus a hand-rolled one) at its own 12/16
// where the DS asks for 14/20. Design QA: Monday 12733122470.
export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const form = useChangePasswordFlow(onClose);

  const onEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void form.submit();
  };

  return (
    <Modal
      open
      onClose={form.cancel}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      title={t('settings.changePassword.title')}
      labelledBy="change-password-title"
      headerSeparator
      footerSeparator
      footer={
        <>
          <Button variant="outlined" onClick={form.cancel} disabled={form.busy}>
            {t('common.close')}
          </Button>
          <Button onClick={form.submit} disabled={!form.canSave} loading={form.busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div className={styles.field}>
          <SecureInput
            label={t('settings.changePassword.currentPassword')}
            autoFocus
            autoComplete="current-password"
            value={form.current}
            onChange={(v) => form.setCurrent(v)}
            onKeyDown={onEnter}
            disabled={form.busy}
            error={form.currentError}
          />
          <TextButton
            className={styles.forgot}
            size="small"
            href={form.forgotUrl}
            external
            target="_blank"
            rel="noreferrer"
            onClick={form.onForgotPassword}
          >
            {t('settings.changePassword.forgotPassword')}
          </TextButton>
        </div>

        <SecureInput
          label={t('settings.changePassword.newPassword')}
          autoComplete="new-password"
          value={form.newPassword}
          onChange={form.setNewPassword}
          onKeyDown={onEnter}
          disabled={form.busy}
          error={form.newPasswordError}
        />

        {/* Revealed once there's something to score, exactly like native — and a SIBLING of the field,
            not a child of it: Figma stacks the card 16 below the input (12815:192141 puts it in the same
            16 rhythm as the fields), where a `.field` wrapper would have given it the field's own 6. */}
        {form.newPassword ? <PasswordStrengthMeter strength={form.strength} /> : null}

        <SecureInput
          label={t('settings.changePassword.confirmPassword')}
          autoComplete="new-password"
          value={form.confirm}
          onChange={form.setConfirm}
          onKeyDown={onEnter}
          disabled={form.busy}
          error={form.mismatchError}
        />

        {form.generalError ? (
          <p className={styles.errorLine} role="alert">
            <AlertIcon />
            {form.generalError}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * One of the three password fields: the DS `Input` on `kind="secure"`, which supplies the masking, the
 * eye toggle, the label and the error row. This wrapper adds NOTHING to the field — it exists only to
 * spell `value`/`error` as this form's hook hands them over and to name the reveal button once for all
 * three.
 */
function SecureInput({
  value,
  onChange,
  error,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error: string | null;
  autoFocus?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const { t } = useTranslation();

  return (
    <Input
      {...rest}
      kind="secure"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      status={error ? 'error' : 'default'}
      statusMessage={error ?? undefined}
      revealAriaLabel={t('settings.changePassword.showPassword')}
      hideAriaLabel={t('settings.changePassword.hidePassword')}
    />
  );
}

/** The submit-time failure, which belongs to the FORM rather than to any one field. */
function AlertIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="7" fill="currentColor" />
      <path d="M8 4.5v4.2" stroke="var(--ds-surface-sunken)" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="11.3" r="0.9" fill="var(--ds-surface-sunken)" />
    </svg>
  );
}
